import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { useI18n, SUPPORTED } from "@/i18n";
import { cn } from "@/lib/utils";

const CODE = { nl: "NL", en: "EN", fr: "FR", es: "ES" };
const NAME = { nl: "Nederlands", en: "English", fr: "Français", es: "Español" };

/**
 * Compact language dropdown. Closed state shows only the active language code (e.g. "NL ▾").
 * `variant`: "light" (public header / cards) or "dark" (mobile menu / customer sidebar footer).
 * Behavior is unchanged: switching updates the UI language, persists to sessionStorage rep_lang
 * (kept for the whole browsing session; every new session starts in English),
 * and on language-prefixed public URLs (/nl /en /fr /es incl. legal pages) also navigates so
 * the URL and the saved language stay in sync (NL is canonical without a prefix).
 */
export function LanguageSelector({ variant = "light", className }) {
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef(null);
  const dark = variant === "dark";

  // Close on outside click/tap and on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Close when the route changes (e.g. language-prefixed navigation).
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const pick = (code) => {
    setLang(code);
    setOpen(false);
    const m = location.pathname.match(/^\/(nl|en|fr|es)(\/.*)?$/);
    if (m) {
      const rest = m[2] || "";
      navigate(code === "nl" ? rest || "/" : `/${code}${rest}`);
    }
  };

  return (
    <div ref={rootRef} className={cn("relative", className)} data-testid="language-selector">
      <button
        type="button"
        onClick={() => {
          // Open upward when there isn't enough room below (e.g. bottom of the mobile menu).
          if (!open && rootRef.current) {
            const rect = rootRef.current.getBoundingClientRect();
            setDropUp(rect.bottom + 200 > window.innerHeight);
          }
          setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("common.language")}
        data-testid="language-selector-toggle"
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-re-red/60",
          dark
            ? "border-white/25 text-white/90 hover:border-white/60 hover:text-white"
            : "border-re-line bg-white text-re-charcoal hover:border-re-charcoal/50"
        )}
      >
        {CODE[lang]}
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180", dark ? "text-white/70" : "text-re-muted")} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t("common.language")}
          data-testid="language-selector-menu"
          className={cn(
            "absolute right-0 z-50 min-w-[9.5rem] overflow-hidden rounded-xl border border-re-line bg-white py-1 shadow-lg shadow-black/10",
            dropUp ? "bottom-full mb-1.5" : "top-full mt-1.5"
          )}
        >
          {SUPPORTED.map((code) => {
            const active = code === lang;
            return (
              <button
                key={code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => pick(code)}
                data-testid={`lang-${code}`}
                className={cn(
                  "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors",
                  "focus-visible:outline-none focus-visible:bg-re-soft",
                  active ? "font-semibold text-re-red" : "text-re-charcoal hover:bg-re-soft"
                )}
              >
                <span className="flex items-baseline gap-2">
                  <span className={cn("text-xs font-semibold", active ? "text-re-red" : "text-re-muted")}>{CODE[code]}</span>
                  <span>{NAME[code]}</span>
                </span>
                {active && <Check className="h-3.5 w-3.5 shrink-0 text-re-red" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
